import { t } from 'i18next';
import { Box3, Quaternion, Vector3, type Camera } from 'three';

import { calculateFullQuaternion } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    getBoundsAxis,
    getPanelDimension,
    isRotation90Degrees
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/panelReplacementGridUtils';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { PartPlacement } from 'api/models';
import type {
    Axis,
    CoverAreaBounds,
    EdgeHandle,
    PanelDimension,
    PanelGridItem,
    PanelReplacementDirection,
    SelectedPanel,
    SnapGridAxes
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

export * from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/panelReplacementGridUtils';

const axisVectors: Record<Axis, Vector3> = {
    x: new Vector3(1, 0, 0),
    y: new Vector3(0, 1, 0),
    z: new Vector3(0, 0, 1)
};
const mouseMargin = 0.12;
const snapMarkHalfLength = 0.28;
const snapMarkHitTolerancePx = 12;
const screenDirections: PanelReplacementDirection[] = ['left', 'right', 'up', 'down'];
export const PANEL_REPLACEMENT_SNAP_HYSTERESIS = 0.005;

export function getPartAxisScale(part: Vector3, axis: Axis): number {
    return Math.abs(part[axis]) || 1;
}

function getPhysicalAxis(bounds: CoverAreaBounds, dimension: PanelDimension, rotated90: boolean): Axis {
    if (dimension === 'width') {
        return rotated90 ? bounds.heightAxis : bounds.widthAxis;
    }
    return rotated90 ? bounds.widthAxis : bounds.heightAxis;
}

function getNormalAxis(bounds: CoverAreaBounds): Axis {
    return bounds.widthAxis !== 'x' && bounds.heightAxis !== 'x'
        ? 'x'
        : bounds.widthAxis !== 'y' && bounds.heightAxis !== 'y'
          ? 'y'
          : 'z';
}

export function getPanelNormalOffset(
    state: PlacementState,
    articleId: string,
    partName: string,
    bounds: CoverAreaBounds
): number {
    const slots = state.articleGeometrySlots.get(articleId);
    const { physical, sheet } = state.batchedMeshes;
    const geometryBounds = new Box3();
    const slotBounds = new Box3();
    if (slots && physical) {
        for (const slot of slots.physicalSlots) {
            if (slot.partName === partName) {
                physical.getBoundingBoxAt(slot.slotId, slotBounds);
                geometryBounds.union(slotBounds);
            }
        }
    }
    if (slots?.sheetPartName === partName && slots.sheetSlotId !== null && sheet) {
        sheet.getBoundingBoxAt(slots.sheetSlotId, slotBounds);
        geometryBounds.union(slotBounds);
    }
    if (geometryBounds.isEmpty()) {
        return 0;
    }

    const axis = getNormalAxis(bounds);
    const coverCenter = (bounds[axis].min + bounds[axis].max) / 2;
    const physicalCenter = (geometryBounds.min[axis] + geometryBounds.max[axis]) / 2;
    const depth = geometryBounds.max[axis] - geometryBounds.min[axis];
    return ((physicalCenter < coverCenter ? -1 : 1) * depth) / 2;
}

function pointAtEdge(bounds: CoverAreaBounds, axis: Axis, sign: -1 | 1): Vector3 {
    const point = new Vector3(
        (bounds.x.min + bounds.x.max) / 2,
        (bounds.y.min + bounds.y.max) / 2,
        (bounds.z.min + bounds.z.max) / 2
    );
    const range = getBoundsAxis(bounds, axis);
    point[axis] = sign < 0 ? range.min : range.max;
    return point;
}

function toWorldPoint(point: Vector3, partPosition: Vector3, partScale: Vector3, quaternion: Quaternion): Vector3 {
    return point.multiply(partScale).applyQuaternion(quaternion).add(partPosition);
}

export function buildEdgeHandles(
    item: PanelGridItem,
    part: PlacementState['articles'][string]['articleInstances'][string]['parts'][string],
    panelNormalOffset = 0
): EdgeHandle[] {
    const dimensions: PanelDimension[] = ['width', 'height'];
    return dimensions.flatMap((dimension) =>
        ([-1, 1] as const).map((sign) => {
            const rotated90 = isRotation90Degrees(part.rotation);
            const localAxis = getPhysicalAxis(item.bounds, dimension, rotated90);
            const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
            const worldAxis = axisVectors[localAxis].clone().applyQuaternion(quaternion).normalize();
            const normalLocalAxis = getNormalAxis(item.bounds);
            const normalWorldAxis = axisVectors[normalLocalAxis].clone().applyQuaternion(quaternion).normalize();
            const normalOffset = normalWorldAxis
                .clone()
                .multiplyScalar(panelNormalOffset * part.scale[normalLocalAxis]);
            const anchor = toWorldPoint(
                pointAtEdge(item.bounds, localAxis, sign),
                part.position,
                part.scale,
                quaternion
            );
            const edgeOffset = Math.max(mouseMargin, getPanelDimension(item, dimension, rotated90) * 0.03);
            const position = anchor
                .clone()
                .addScaledVector(worldAxis, sign * edgeOffset * getPartAxisScale(part.scale, localAxis));

            return { dimension, sign, localAxis, anchor, position, worldAxis, normalWorldAxis, normalOffset };
        })
    );
}

export function getEdgeKey(edge: EdgeHandle): string {
    return `${edge.dimension}-${edge.sign}`;
}

export function getScreenDirections(
    handles: EdgeHandle[],
    camera: Camera
): Record<string, PanelReplacementDirection> {
    const projected = handles.map((candidate) => ({
        candidate,
        point: candidate.position.clone().project(camera)
    }));
    const minX = Math.min(...projected.map(({ point }) => point.x));
    const maxX = Math.max(...projected.map(({ point }) => point.x));
    const minY = Math.min(...projected.map(({ point }) => point.y));
    const maxY = Math.max(...projected.map(({ point }) => point.y));
    const getDistance = (index: number, direction: PanelReplacementDirection): number => {
        const { point } = projected[index];
        switch (direction) {
            case 'left':
                return Math.abs(point.x - minX);
            case 'right':
                return Math.abs(point.x - maxX);
            case 'up':
                return Math.abs(point.y - maxY);
            case 'down':
                return Math.abs(point.y - minY);
        }
    };

    let bestCost = Number.POSITIVE_INFINITY;
    let bestSeparation = -1;
    let bestAssignment: PanelReplacementDirection[] = [];
    const assignDirections = (
        index: number,
        remaining: PanelReplacementDirection[],
        assignment: PanelReplacementDirection[],
        cost: number
    ) => {
        if (index === projected.length) {
            const tied = Math.abs(cost - bestCost) <= Number.EPSILON * handles.length;
            if (cost < bestCost || tied) {
                const controlPositions = handles.map((handle, handleIndex) =>
                    getControlPosition(handle, assignment[handleIndex], camera).project(camera).setZ(0)
                );
                const separation = Math.min(
                    ...controlPositions.flatMap((point, pointIndex) =>
                        controlPositions.slice(pointIndex + 1).map((other) => point.distanceToSquared(other))
                    )
                );
                if (tied && separation <= bestSeparation) {
                    return;
                }
                bestCost = cost;
                bestSeparation = separation;
                bestAssignment = assignment;
            }
            return;
        }

        for (const direction of remaining) {
            assignDirections(
                index + 1,
                remaining.filter((candidate) => candidate !== direction),
                [...assignment, direction],
                cost + getDistance(index, direction)
            );
        }
    };
    assignDirections(0, screenDirections, [], 0);

    return Object.fromEntries(projected.map(({ candidate }, index) => [getEdgeKey(candidate), bestAssignment[index]]));
}

export function getScreenEdge(
    handles: EdgeHandle[],
    camera: Camera,
    direction: PanelReplacementDirection
): EdgeHandle {
    const directions = getScreenDirections(handles, camera);
    return handles.find((handle) => directions[getEdgeKey(handle)] === direction) ?? handles[0];
}

export function getReplacementParts(
    source: PanelGridItem,
    target: PanelGridItem,
    instance: PlacementState['articles'][string]['articleInstances'][string],
    edge: EdgeHandle
): PartPlacement[] | null {
    const sourceNames = Object.keys(instance.parts);
    if (sourceNames.length !== target.partNames.length) {
        return null;
    }

    const sourceNamesByTarget = target.partNames.map((name, index) =>
        Object.prototype.hasOwnProperty.call(instance.parts, name) ? name : sourceNames[index]
    );
    if (sourceNamesByTarget.some((name) => !instance.parts[name])) {
        return null;
    }

    const sourceAnchor = instance.parts[sourceNames[0]];
    const quaternion = calculateFullQuaternion(sourceAnchor.alignment, sourceAnchor.rotation);
    const targetAxis = getPhysicalAxis(target.bounds, edge.dimension, isRotation90Degrees(sourceAnchor.rotation));
    const fixedEdgeSign: -1 | 1 = edge.sign === 1 ? -1 : 1;
    const sourceEdge = pointAtEdge(source.bounds, edge.localAxis, fixedEdgeSign);
    const targetAxisVector = axisVectors[targetAxis].clone().applyQuaternion(quaternion);
    const sourceEdgeWorld = toWorldPoint(sourceEdge, sourceAnchor.position, sourceAnchor.scale, quaternion);
    const sourceDirection: -1 | 1 = edge.worldAxis.dot(targetAxisVector) < 0 ? edge.sign : fixedEdgeSign;
    const targetEdge = pointAtEdge(target.bounds, targetAxis, sourceDirection);
    const targetEdgeWorld = toWorldPoint(targetEdge, sourceAnchor.position, sourceAnchor.scale, quaternion);
    const positionDelta = sourceEdgeWorld.sub(targetEdgeWorld);

    return target.partNames.map((name, index) => {
        const part = instance.parts[sourceNamesByTarget[index]];
        const position = part.position.clone().add(positionDelta);
        return {
            name,
            position: { x: position.x, y: position.y, z: position.z },
            alignment: { x: part.alignment.x, y: part.alignment.y, z: part.alignment.z },
            scale: { x: part.scale.x, y: part.scale.y, z: part.scale.z },
            rotation: part.rotation
        };
    });
}

export function getPointerAxisDistance(
    event: PointerEvent,
    canvas: HTMLCanvasElement,
    camera: Camera,
    origin: Vector3,
    gridAxis: Vector3
): number | undefined {
    const rect = canvas.getBoundingClientRect();
    const anchor = origin.clone().project(camera);
    const axisEnd = origin.clone().add(gridAxis).project(camera);
    const axisX = ((axisEnd.x - anchor.x) * rect.width) / 2;
    const axisY = (-(axisEnd.y - anchor.y) * rect.height) / 2;
    const axisLengthSquared = axisX * axisX + axisY * axisY;
    if (axisLengthSquared <= Number.EPSILON) {
        return undefined;
    }

    const anchorX = rect.left + ((anchor.x + 1) * rect.width) / 2;
    const anchorY = rect.top + ((1 - anchor.y) * rect.height) / 2;
    return ((event.clientX - anchorX) * axisX + (event.clientY - anchorY) * axisY) / axisLengthSquared;
}

function getCameraDirectionAxis(direction: PanelReplacementDirection, camera: Camera): Vector3 {
    const cameraRight = new Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    const cameraUp = new Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
    switch (direction) {
        case 'left':
            return cameraRight.negate();
        case 'right':
            return cameraRight;
        case 'up':
            return cameraUp;
        case 'down':
            return cameraUp.negate();
    }
}

export function getSnapGridAxes(
    edge: EdgeHandle,
    direction: PanelReplacementDirection,
    camera: Camera
): SnapGridAxes {
    const viewDirection = camera.getWorldDirection(new Vector3());
    const gridAxis = getCameraDirectionAxis(direction, camera);
    if (isFrontBackView(edge, camera)) {
        const projectedAxis = edge.worldAxis
            .clone()
            .multiplyScalar(edge.sign)
            .projectOnPlane(viewDirection)
            .normalize();
        if (projectedAxis.dot(gridAxis) < 0) {
            projectedAxis.negate();
        }
        gridAxis.copy(projectedAxis);
    }
    return {
        origin: getControlAnchor(edge, camera),
        gridAxis,
        markerAxis: viewDirection.cross(gridAxis).normalize()
    };
}

function isFrontBackView(edge: EdgeHandle, camera: Camera): boolean {
    const viewDirection = camera.getWorldDirection(new Vector3());
    const normalAlignment = Math.abs(edge.normalWorldAxis.dot(viewDirection));
    const widthAlignment = Math.abs(edge.worldAxis.dot(viewDirection));
    const otherAxis = edge.normalWorldAxis.clone().cross(edge.worldAxis);
    const heightAlignment = Math.abs(otherAxis.dot(viewDirection));
    return normalAlignment >= Math.max(widthAlignment, heightAlignment);
}

function getControlAnchor(edge: EdgeHandle, camera: Camera): Vector3 {
    return isFrontBackView(edge, camera) ? edge.anchor.clone() : edge.anchor.clone().add(edge.normalOffset);
}

export function getControlPosition(
    edge: EdgeHandle,
    direction: PanelReplacementDirection,
    camera: Camera
): Vector3 {
    return getControlAnchor(edge, camera).addScaledVector(getCameraDirectionAxis(direction, camera), mouseMargin);
}

export function getSnapMarkPoints(
    candidate: PanelGridItem,
    current: PanelGridItem,
    edge: EdgeHandle,
    rotated90: boolean,
    scale: number,
    origin: Vector3,
    gridAxis: Vector3,
    markerAxis: Vector3
): [Vector3, Vector3] {
    const currentDimension = getPanelDimension(current, edge.dimension, rotated90);
    const candidateDimension = getPanelDimension(candidate, edge.dimension, rotated90);
    const center = origin.clone().addScaledVector(gridAxis, (candidateDimension - currentDimension) * scale);
    return [
        center.clone().addScaledVector(markerAxis, -snapMarkHalfLength),
        center.clone().addScaledVector(markerAxis, snapMarkHalfLength)
    ];
}

function getScreenPointSegmentDistance(
    x: number,
    y: number,
    start: Vector3,
    end: Vector3,
    camera: Camera,
    rect: DOMRect
): number {
    const startNdc = start.clone().project(camera);
    const endNdc = end.clone().project(camera);
    const startX = rect.left + ((startNdc.x + 1) * rect.width) / 2;
    const startY = rect.top + ((1 - startNdc.y) * rect.height) / 2;
    const endX = rect.left + ((endNdc.x + 1) * rect.width) / 2;
    const endY = rect.top + ((1 - endNdc.y) * rect.height) / 2;
    const segmentX = endX - startX;
    const segmentY = endY - startY;
    const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
    const projection =
        segmentLengthSquared === 0
            ? 0
            : Math.max(0, Math.min(1, ((x - startX) * segmentX + (y - startY) * segmentY) / segmentLengthSquared));
    return Math.hypot(x - (startX + projection * segmentX), y - (startY + projection * segmentY));
}

export function getClickedSnapTarget(
    event: PointerEvent,
    canvas: HTMLCanvasElement,
    camera: Camera,
    current: PanelGridItem,
    candidates: PanelGridItem[],
    edge: EdgeHandle,
    rotated90: boolean,
    scale: number,
    direction: PanelReplacementDirection
): PanelGridItem | undefined {
    const { origin, gridAxis, markerAxis } = getSnapGridAxes(edge, direction, camera);
    const rect = canvas.getBoundingClientRect();
    return candidates.find((candidate) => {
        const [start, end] = getSnapMarkPoints(
            candidate,
            current,
            edge,
            rotated90,
            scale,
            origin,
            gridAxis,
            markerAxis
        );
        return (
            getScreenPointSegmentDistance(event.clientX, event.clientY, start, end, camera, rect) <=
            snapMarkHitTolerancePx
        );
    });
}

export function replacePanel(
    state: PlacementState,
    source: SelectedPanel,
    target: PanelGridItem,
    edge: EdgeHandle
): boolean {
    const parts = getReplacementParts(source.item, target, source.instance, edge);
    if (!parts) {
        toastNotifications.error({ title: t('error'), message: t('errorPanelReplacementFailed') });
        return false;
    }

    state.batchAddArticles([
        {
            id: target.article.id,
            articleNumber: target.article.articleNumber,
            lifeCycleCode: target.article.lifeCycleCode
        }
    ]);
    const replacementId = state.replaceArticleInstance(source.articleId, target.article.id, source.instanceId, parts);
    if (!replacementId) {
        toastNotifications.error({ title: t('error'), message: t('errorPanelReplacementFailed') });
        return false;
    }
    return true;
}
